import { HttpClient, HttpErrorResponse } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { Observable, catchError, throwError } from "rxjs";
import { ListResult } from "../api/core/list-result";
import { ForeignNational } from "../api/foreign-national/foreign-national";
import { BACKEND_URL } from "../app.config";
import { ForeignNationalRelative } from "../api/foreign-national/relative";
import {
  Task,
  ForeignNationalEducation,
  ForeignNationalPassport,
  ForeignNationalPriorityDate,
  ForeignNationalVisa,
  ForeignNationalI94,
  Matter,
  FailedItemInfo,
} from "../api/data-model";
import { PagedResultDto } from "@abp/ng.core";
import { ForeignNationalDocument } from "../api/foreign-national/document";
import { ForeignNationalAddress } from "../api/foreign-national/address";
import { ForeignNationalDates } from "../api/foreign-national/dates";
import { OtherName } from "../api/foreign-national/other-name.module";
import { StayOfPeriod } from "../api/foreign-national/stay-of-period";
import { FNWorkHistory } from "../api/foreign-national/work-history";
import {
  CreateFNCompanyAssociationDto,
  FNCompanyAssociation,
  UpdateFNCompanyAssociationDto,
} from "../api/foreign-national/foreign-national-fn-company-association.types";
import { EmailLog } from "../api/matter/email-log";

@Injectable()
export class ForeignNationalService {
  constructor(
    private http: HttpClient,
    @Inject(BACKEND_URL) private baseUrl: string,
    private readonly router: Router
  ) { }

  /**
   * Handle 404 error by navigating to not found page
   * @param code The business code that was not found
   * @returns An empty Observable that never completes
   */
  private handle404Error<T>(code: string): (error: HttpErrorResponse) => Observable<T> {
    return (error: HttpErrorResponse) => {
      if (error.status === 404) {
        // Navigate to 404 page with context information
        this.router.navigate(['/notfound'], {
          queryParams: {
            type: 'foreign-national',
            code: code,
            message: `Foreign National with code "${code}" not found`
          }
        });
        // Return an empty Observable that never completes to prevent further processing
        return new Observable<T>(() => { });
      }
      return throwError(() => error);
    };
  }

  getList(
    filter: any,
    sorting: string,
    skipCount: number,
    maxResultCount: number
  ): Observable<ListResult<ForeignNational>> {
    return this.http.get<ListResult<ForeignNational>>(
      this.baseUrl + "/api/app/foreign-national",
      {
        params: {
          companyId: filter.companyId ?? "",
          businessCode: filter.businessCode ?? "",
          firstName: filter.firstName ?? "",
          lastName: filter.lastName ?? "",
          fnVisaType: filter.fnVisaType ?? "",
          activeStatus: filter.activeStatus ?? "",
          email: filter.email ?? "",
          sorting,
          skipCount,
          maxResultCount,
        },
      }
    );
  }

  exportToCsv(filter: any, sorting: string): Observable<Blob> {
    return this.http.get(
      this.baseUrl + "/api/app/foreign-national/exporting",
      {
        responseType: "blob",
        params: {
          companyId: filter.companyId ?? "",
          businessCode: filter.businessCode ?? "",
          firstName: filter.firstName ?? "",
          lastName: filter.lastName ?? "",
          fnVisaType: filter.fnVisaType ?? "",
          activeStatus: filter.activeStatus ?? "",
          email: filter.email ?? "",
          sorting,
        },
      }
    );
  }

  getMatters(id: string): Observable<Matter[]> {
    return this.http.get<Matter[]>(
      `${this.baseUrl}/api/app/foreign-national/${id}/matters`
    );
  }

  getFN(id: string): Observable<ForeignNational> {
    if (!id) {
      return throwError(() => new Error('Foreign National ID is required'));
    }
    return this.http.get<ForeignNational>(
      this.baseUrl + "/api/app/foreign-national/" + id
    );
  }

  getFNByCode(code: string): Observable<ForeignNational> {
    return this.http.get<ForeignNational>(
      this.baseUrl + "/api/app/foreign-national/by-code?code=" + code
    ).pipe(
      catchError(this.handle404Error<ForeignNational>(code))
    );
  }

  getFNIdByCode(code: string): Observable<string> {
    return this.http.get<string>(
      this.baseUrl + "/api/app/foreign-national/id-by-code?code=" + code
    ).pipe(
      catchError(this.handle404Error<string>(code))
    );
  }

  getRelatives(id: string): Observable<ForeignNationalRelative[]> {
    return this.http.get<ForeignNationalRelative[]>(
      `${this.baseUrl}/api/app/foreign-national/${id}/relatives`
    );
  }

  getInverseRelatives(id: string): Observable<ForeignNationalRelative[]> {
    return this.http.get<ForeignNationalRelative[]>(
      `${this.baseUrl}/api/app/foreign-national/${id}/inverse-relatives`
    );
  }

  updateRelatives(fnId: string, data: any) {
    return this.http.put<ForeignNationalRelative>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/relatives`,
      data
    );
  }

  createRelatives(fnId: string, data: any) {
    return this.http.post<ForeignNationalRelative>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/relatives`,
      data
    );
  }

  deleteRelatives(fnId: string, relativeId: string) {
    return this.http.delete(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/relatives/${relativeId}`
    );
  }

  getTasks(
    id: string,
    onlyNotCompleted: boolean,
    skipCount: number,
    maxResultCount: number,
    sorting: string
  ): Observable<PagedResultDto<Task>> {
    return this.http.get<PagedResultDto<Task>>(
      `${this.baseUrl}/api/app/foreign-national/${id}/tasks`,
      {
        params: {
          id,
          onlyNotCompleted,
          skipCount,
          maxResultCount,
          sorting,
        },
      }
    );
  }

  updateFN(fnId: string, data: any) {
    return this.http.put<ForeignNational>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/information`,
      data
    );
  }

  markFNForDeltion(fnId: string): Observable<number> {
    return this.http.put<number>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/status-mark-for-deletion`,
      null
    );
  }

  mergeFN(fnId: string, targetFnId: string):Observable<FailedItemInfo[]> {
    return this.http.post<FailedItemInfo[]>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/merge-foreign-nationals/${targetFnId}`,
      null
    );
  }

  checkFNExists(data: any) {
    return this.http.post<boolean>(
      `${this.baseUrl}/api/app/foreign-national/exits`,
      data
    );
  }

  createFN(data: any) {
    return this.http.post<ForeignNational>(
      `${this.baseUrl}/api/app/foreign-national`,
      data
    );
  }

  //#region documents expiration
  getDocumentExpiration(fnId: string) {
    return this.http.get<ForeignNationalDocument[]>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/document-expiration`
    );
  }

  updateDocumentsExpiration(fnId: string, documents: any[]) {
    return this.http.put<ForeignNationalDocument[]>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/document-expiration`,
      documents
    );
  }
  //#endregion documents expiration

  //#endregion address
  getAddresses(fnId: string) {
    return this.http.get<ForeignNationalAddress[]>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/addresses`
    );
  }

  updateFNAddress(fnId: string, data: any) {
    return this.http.put<ForeignNationalAddress>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/address/${data.id}`,
      data
    );
  }

  createFNAddress(fnId: string, data: any) {
    return this.http.post<ForeignNationalAddress>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/address`,
      data
    );
  }

  setPrimaryFNAddress(fnId: string, addressId: string) {
    return this.http.post<ForeignNationalAddress>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/set-primary-address/${addressId}`,
      null
    );
  }

  deleteFNAddress(fnId: string, addressId: string) {
    return this.http.delete(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/address/${addressId}`
    );
  }
  //#endregion address

  //#region visa
  getVisas(fnId: string) {
    return this.http.get<ForeignNationalVisa[]>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/visas`
    );
  }

  updateFNVisa(fnId: string, data: any) {
    return this.http.put<ForeignNationalVisa>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/visa/${data.id}`,
      data
    );
  }

  createFNVisa(fnId: string, data: any) {
    return this.http.post<ForeignNationalVisa>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/visa`,
      data
    );
  }

  setPrimaryFNVisa(fnId: string, visaId: string) {
    return this.http.post<ForeignNationalVisa>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/set-primary-visa/${visaId}`,
      null
    );
  }

  deleteFNVisa(fnId: string, visaId: string) {
    return this.http.delete(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/visa/${visaId}`
    );
  }
  //#endregion visa

  //#region passports
  getPassports(fnId: string) {
    return this.http.get<ForeignNationalPassport[]>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/passports`
    );
  }

  updateFNPassport(fnId: string, data: any) {
    return this.http.put<ForeignNationalPassport>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/passport/${data.id}`,
      data
    );
  }

  createFNPassport(fnId: string, data: any) {
    return this.http.post<ForeignNationalPassport>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/passport`,
      data
    );
  }

  setPrimaryFNPassport(fnId: string, passportId: string) {
    return this.http.post<ForeignNationalPassport>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/set-primary-passport/${passportId}`,
      null
    );
  }

  deleteFNPassport(fnId: string, passportId: string) {
    return this.http.delete(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/passport/${passportId}`
    );
  }
  //#endregion passport

  //#region priority dates
  getPriorityDates(fnId: string) {
    return this.http.get<ForeignNationalPriorityDate[]>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/priority-dates`
    );
  }

  updateFNPriorityDate(fnId: string, data: any) {
    return this.http.put<ForeignNationalPriorityDate>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/priority-date/${data.id}`,
      data
    );
  }

  createFNPriorityDate(fnId: string, data: any) {
    return this.http.post<ForeignNationalPriorityDate>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/priority-date`,
      data
    );
  }

  deleteFNPriorityDate(fnId: string, priorityDateId: string) {
    return this.http.delete(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/priority-date/${priorityDateId}`
    );
  }
  //#endregion priority dates

  //#region fn dates
  getFNDates(fnId: string) {
    return this.http.get<ForeignNationalDates>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/dates`
    );
  }
  updateFNDates(fnId: string, data: any) {
    return this.http.put<ForeignNationalDates>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/dates`,
      data
    );
  }
  //#endregion fn dates

  //#region fn education
  getFNEducationList(fnId: string, sorting: string, skipCount: number, maxResultCount: number): Observable<ListResult<ForeignNationalEducation>> {
    return this.http.get<ListResult<ForeignNationalEducation>>(`${this.baseUrl}/api/app/foreign-national/${fnId}/educations`, {
      params: {
        fnId,
        sorting,
        skipCount,
        maxResultCount,
      },
    });
  }

  addFNEducation(fnId: string, fnEducation: ForeignNationalEducation): Observable<ForeignNationalEducation> {
    return this.http.post<ForeignNationalEducation>(`${this.baseUrl}/api/app/foreign-national/${fnId}/education`, fnEducation);
  }

  updateFNEducation(fnId: string, id: string, fnEducation: ForeignNationalEducation): Observable<ForeignNationalEducation> {
    return this.http.put<ForeignNationalEducation>(`${this.baseUrl}/api/app/foreign-national/${fnId}/education/${id}`, fnEducation);
  }

   deleteEducation(fnId: string, educationId: string) {
    return this.http.delete(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/education/${educationId}`
    );
  }
  updateEducationPrimary(id: string, educationId: string): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}/api/app/foreign-national/${id}/education-primary/${educationId}`, null);
  }
  //#endregion fn other names

  getFnOtherNames(fnId: string) {
    return this.http.get<OtherName[]>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/other-name`
    );
  }

  updateFnOtherNames(id: string, data: OtherName) {
    return this.http.put<OtherName>(
      `${this.baseUrl}/api/app/foreign-national/${id}/other-name`,
      data
    );
  }

  createFnOtherNames(fnId: string, data: OtherName) {
    return this.http.post<OtherName>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/other-name`,
      data
    );
  }

  deleteFNOtherName(otherNameId: string) {
    return this.http.delete(
      `${this.baseUrl}/api/app/foreign-national/${otherNameId}/other-name`
    );
  }

  //#endregion fn stay of periods

  getFnStayOfPeriods(fnId: string) {
    return this.http.get<StayOfPeriod[]>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/stay-periods`
    );
  }
  updateFnStayOfPeriods(id: string, data: StayOfPeriod) {
    return this.http.put<StayOfPeriod>(
      `${this.baseUrl}/api/app/foreign-national/${id}/stay-period`,
      data
    );
  }

  createFnStayOfPeriods(fnId: string, data: StayOfPeriod) {
    return this.http.post<StayOfPeriod>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/stay-period`,
      data
    );
  }

  deleteFNStayOfPeriod(stayOfPeriodId: string) {
    return this.http.delete(
      `${this.baseUrl}/api/app/foreign-national/${stayOfPeriodId}/stay-period`
    );
  }

  getFnWorkHistories(fnId: string) {
    return this.http.get<FNWorkHistory[]>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/work-histories`
    );
  }

  getFnWorkHistory(id: string): Observable<FNWorkHistory> {
    return this.http.get<FNWorkHistory>(
      `${this.baseUrl}/api/app/foreign-national/${id}/work-history`
    );
  }
  updateFnWorkHistory(id: string, data: FNWorkHistory) {
    return this.http.put<FNWorkHistory>(
      `${this.baseUrl}/api/app/foreign-national/${id}/work-history`,
      data
    );
  }
  createFnWorkHistory(fnId: string, data: FNWorkHistory) {
    return this.http.post<FNWorkHistory>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/work-history`,
      data
    );
  }

  deleteFNWorkHistory(workHistoryId: string) {
    return this.http.delete(
      `${this.baseUrl}/api/app/foreign-national/${workHistoryId}/work-history`
    );
  }

  getFnCompanyAssociations(foreignNationalId: string) {
    return this.http.get<FNCompanyAssociation[]>(
      `${this.baseUrl}/api/app/foreign-national/${foreignNationalId}/company-associations`
    );
  }

  getFnCompanyAssociation(associationId: string): Observable<FNCompanyAssociation> {
    return this.http.get<FNCompanyAssociation>(
      `${this.baseUrl}/api/app/foreign-national/${associationId}/company-association`
    );
  }


  createFnCompanyAssociation(foreignNationalId: string, data: CreateFNCompanyAssociationDto) {
    return this.http.post<FNCompanyAssociation>(
      `${this.baseUrl}/api/app/foreign-national/${foreignNationalId}/company-association`,
      data
    );
  }

 
  updateFnCompanyAssociation(associationId: string, data: UpdateFNCompanyAssociationDto) {
    return this.http.put<FNCompanyAssociation>(
      `${this.baseUrl}/api/app/foreign-national/${associationId}/company-association`,
      data
    );
  }

  //I94
  getFNFormI94(fnId: string): Observable<ForeignNationalI94> {
    if (!fnId) {
      return throwError(() => new Error('Foreign National ID is required'));
    }
    return this.http.get<ForeignNationalI94>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/form-i94`,
    );
  }

  updateFNFormI94(fnId: string, data: ForeignNationalI94) {
    return this.http.put<ForeignNationalI94>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/form-i94`,
      data
    );
  }

  createFNFormI94(fnId: string, data: ForeignNationalI94) {
    return this.http.post<ForeignNationalI94>(
      `${this.baseUrl}/api/app/foreign-national/${fnId}/form-i94`,
      data
    );
  }

  ensureSharePointStructure(id: string): Observable<void> {
    return this.http.post<void>(
      `${this.baseUrl}/api/app/foreign-national/${id}/ensure-sharepoint-structure`,
      null
    );
  }

    getEmailLog(id: string): Observable<EmailLog[]> {
    return this.http.get<EmailLog[]>(
      `${this.baseUrl}/api/app/foreign-national/${id}/email-log-by-fNId`
    );
  }
    getEmailLogById(id: string): Observable<EmailLog> {
    return this.http.get<EmailLog>(
      `${this.baseUrl}/api/app/foreign-national/${id}/email-log-by-id`
    );
  }
}
